import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { ButtonLoader } from "Components";
import { toast } from "react-toastify";
import { newAuthService } from "Service/Axios";
import { saveUserProfileUrl } from "Constant/api";
import { VIEW_PROFILE_SECTIONS } from "Constant/constants";
import { Validator } from "Utils/validator";

const GrantRoles = [
  "Principal Investigator",
  "Co-Principal Role Investigator (Co-PI)",
  "Investigator",
  "Collaborator",
  "Other",
];

export const GrantModal = ({ show, grantData, toggle, fetchData }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    gid: "",
    titleOfTheGrant: "",
    grantRole: "",
    otherGrantRole: "",
    grantNumber: "",
    fundingAgency: "",
    grantStartDate: "",
    grantEndDate: "",
    totalAmountAwarded: "",
    grantProjectDescription: "",
    grantWebsiteLink: "",
  });

  const [errMsg, setErrMsg] = useState({
    titleOfTheGrant: "",
    grantRole: "",
    fundingAgency: "",
    grantStartDate: "",
    grantEndDate: "",
  });

  const {
    gid,
    titleOfTheGrant,
    grantRole,
    otherGrantRole,
    grantNumber,
    fundingAgency,
    grantStartDate,
    grantEndDate,
    totalAmountAwarded,
    grantProjectDescription,
    grantWebsiteLink,
  } = formData;

  const validationRequiredFields = [
    "titleOfTheGrant",
    "grantRole",
    "fundingAgency",
    "grantStartDate",
    "grantEndDate",
  ];

  useEffect(() => {
    if (grantData) {
      setFormData({
        gid: grantData.gid || "",
        titleOfTheGrant: grantData.title || "",
        grantRole: grantData.role || "",
        otherGrantRole: grantData.otherGrantRole || "",
        grantNumber: grantData.grantNumber || "",
        fundingAgency: grantData.fundingAgency || "",
        grantStartDate: grantData.grantStartDate || "",
        grantEndDate: grantData.grantEndDate || "",
        totalAmountAwarded: grantData.totalAmountAwarded || "",
        grantProjectDescription: grantData.description || "",
        grantWebsiteLink: grantData.grantWebsiteLink || "",
      });
    }
  }, [grantData]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationRequiredFields.includes(name)) {
      if (!Validator.text(value)) {
        setErrMsg((prev) => ({
          ...prev,
          [name]: t(`common.messages.error.viewProfile.grant.${name}.blank`),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      // Validate always-required fields
      for (const field of validationRequiredFields) {
        const value = formData[field];
        if (!Validator.text(value)) {
          errors[field] = t(
            `common.messages.error.viewProfile.experience.${field}.blank`
          );
          isValid = false;
        }
      }

      // Set error messages
      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  const onSaveGrant = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          key: VIEW_PROFILE_SECTIONS.grant,
          [VIEW_PROFILE_SECTIONS.grant]: {
            title: titleOfTheGrant,
            role: grantRole,
            otherGrantRole,
            grantNumber,
            fundingAgency,
            grantStartDate,
            grantEndDate,
            totalAmountAwarded,
            description: grantProjectDescription,
            grantWebsiteLink,
          },
        };

        if (gid.trim()) {
          payload[VIEW_PROFILE_SECTIONS.grant].gid = gid;
        }

        setLoading(true);
        const response = await newAuthService(
          saveUserProfileUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t("common.messages.notification.success.profileUpdated")
          );
          fetchData();
          toggle();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal size="lg" show={show} onHide={toggle} className="highlightsModal">
      <Modal.Header closeButton>
        <Modal.Title className="text-md font-medium">
          {t(
            `auth.viewProfile.${
              gid ? "editGrantAndFunding" : "addGrantAndFunding"
            }`
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.titleOfTheGrant")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="titleOfTheGrant"
              placeholder={t("common.placeHolder.titleOfTheGrant")}
              value={titleOfTheGrant}
              onChange={handleChange}
            />
            {errMsg.titleOfTheGrant && (
              <p className="error">{errMsg.titleOfTheGrant}</p>
            )}
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.grantRole")}{" "}
              <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              name="grantRole"
              value={grantRole}
              onChange={handleChange}
            >
              <option value="">{t("common.placeHolder.grantRole")}</option>
              {GrantRoles.map((grntRole) => (
                <option value={grntRole} key={grntRole}>
                  {grntRole}
                </option>
              ))}
            </select>
            {grantRole === "Other" && (
              <div className="my-3">
                <input
                  type="text"
                  className="form-control mt-2"
                  name="otherGrantRole"
                  placeholder={t("common.placeHolder.otherGrantRole")}
                  value={otherGrantRole}
                  onChange={handleChange}
                />
              </div>
            )}
            {errMsg.grantRole && <p className="error">{errMsg.grantRole}</p>}
          </div>
          <div className="col-md-12 mb-3">
            <label className="form-label">
              {t("common.label.fundingAgency")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="fundingAgency"
              placeholder={t("common.placeHolder.fundingAgency")}
              value={fundingAgency}
              onChange={handleChange}
            />
            {errMsg.fundingAgency && (
              <p className="error">{errMsg.fundingAgency}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.grantNumber")}
            </label>
            <input
              type="text"
              className="form-control"
              name="grantNumber"
              placeholder={t("common.placeHolder.grantNumber")}
              value={grantNumber}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.totalAmountAwarded")}
            </label>
            <input
              type="number"
              className="form-control"
              name="totalAmountAwarded"
              placeholder={t("common.placeHolder.totalAmountAwarded")}
              value={totalAmountAwarded}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.grantStartDate")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              name="grantStartDate"
              placeholder={t("common.placeHolder.grantStartDate")}
              value={grantStartDate}
              onChange={handleChange}
            />
            {errMsg.grantStartDate && (
              <p className="error">{errMsg.grantStartDate}</p>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("common.label.grantEndDate")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              name="grantEndDate"
              placeholder={t("common.placeHolder.grantEndDate")}
              value={grantEndDate}
              onChange={handleChange}
            />
            {errMsg.grantEndDate && (
              <p className="error">{errMsg.grantEndDate}</p>
            )}
          </div>
          <div className="col-md-12 mb-3">
            <label className="form-label">
              {t("common.label.grantProjectDescription")}
            </label>
            <textarea
              className="form-control"
              name="grantProjectDescription"
              placeholder={t("common.placeHolder.grantProjectDescription")}
              value={grantProjectDescription}
              onChange={handleChange}
              rows={4}
            />
          </div>
          <div className="col-md-12 mb-3">
            <label className="form-label">
              {t("common.label.grantWebsiteLink")}
            </label>
            <input
              type="text"
              className="form-control"
              name="grantWebsiteLink"
              placeholder={t("common.placeHolder.grantWebsiteLink")}
              value={grantWebsiteLink}
              onChange={handleChange}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={toggle}>
          {t("common.button.cancel")}
        </Button>
        {loading ? (
          <ButtonLoader />
        ) : (
          <Button variant="primary" onClick={onSaveGrant}>
            {t("common.button.save")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
